import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Form, ProgressBar, Modal } from 'react-bootstrap';
import axios from 'axios';
import { getInitialLoginData } from '../../../redux/slices/userSlice';
import '../order-tracker.scss';
import StepTracker from './StepTracker';
import Toast, { useToast } from '../../Toast/Toast';

const getExt = (name) => {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i).toLowerCase() : '';
};

// Display order = array order. Stored TypeID 3 Data and the <advertiserCode>.txt are written sorted by
// `index` with the original `label`s, so downstream consumers see exactly the same shape as before.
const QUESTIONS = [
    { name: 'slogan', index: 2, label: 'Slogan', max: 100, prompt: 'What is your business slogan or tagline?' },
    { name: 'productDescription', index: 1, label: 'Product Description', max: 250, prompt: 'What products or services would you like to focus on in your ad?' },
    { name: 'specialOffers', index: 7, label: 'Special Offers', max: 200, prompt: 'Do you have a special offer or promotion you\u2019d like advertised?' },
    { name: 'contactInfo', index: 3, label: 'Contact Info', max: 200, required: true, prompt: 'What contact information should be included in your ad? (required)' },
    { name: 'businessName', index: 4, label: 'Business Name', max: 100, prompt: 'What business name should be displayed in your ad?' },
    { name: 'socialMedia', index: 5, label: 'Social Media', max: 150, prompt: 'Would you like any social media accounts featured?' },
    { name: 'certificationNumbers', index: 6, label: 'Certification Numbers', max: 200, prompt: 'Are there any required certifications, license numbers, ID numbers, or other information that must appear in your ad?' },
    { name: 'productionNotes', index: 8, label: 'Production Notes', max: 150, prompt: 'Production Notes / Special Instructions: Please briefly share any specific messaging or creative requests.' }
];

const WaitingOnAssets = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { initialLoginData } = useSelector((state) => state.user);
    const { user } = useSelector((state) => state.user);
    const account = initialLoginData?.Account;
    const agreements = initialLoginData?.Agreements;
    const { agreementID } = useParams();
    const advertiserCode = account?.AdvertiserCode;
    const fileInputRef = useRef();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [fileProgress, setFileProgress] = useState({});
    const { toastState, showToast, hideToast } = useToast();
    // { ".zip": "<rejection message>", ... } from AdPortalUploadController.UploadRules.
    // If this fails to load, the server still rejects blocked files and the toast shows its message.
    const [blockedExtensions, setBlockedExtensions] = useState({});

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/UploadRules`)
            .then(res => setBlockedExtensions(res.data || {}))
            .catch(err => console.error('Failed to load upload rules:', err));
    }, []);
    const [formData, setFormData] = useState({
        productDescription: '',
        slogan: '',
        contactInfo: '',
        businessName: '',
        socialMedia: '',
        certificationNumbers: '',
        specialOffers: '',
        productionNotes: ''
    });

    const logDiagnostic = async (stage, extraDetails = {}) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/CRM/LogFrontendDiagnostic`,
                {
                    userEmail: user?.Email || user?.email || 'Unknown',
                    stage: stage,
                    details: {
                        // Core validation info
                        hasToken: !!localStorage.getItem('token'),
                        tokenLength: localStorage.getItem('token')?.length || 0,
                        hasUser: !!user,
                        userID: user?.UserID || null,
                        userIDType: typeof user?.UserID,

                        // Agreement ID - the likely culprit
                        agreementIDFromParams: agreementID,
                        agreementIDFromParamsType: typeof agreementID,
                        agreementIDRefCurrent: agreementIDRef.current,
                        agreementIDRefType: typeof agreementIDRef.current,

                        // Context
                        advertiserCode: advertiserCode,
                        url: window.location.href,
                        pathname: window.location.pathname,
                        search: window.location.search,

                        // Form data
                        hasContactInfo: !!formData.contactInfo,
                        contactInfoLength: formData.contactInfo?.length || 0,

                        // Extra details
                        timestamp: new Date().toISOString(),
                        ...extraDetails
                    }
                },
                { timeout: 2000 }
            );
        } catch (err) {
            console.error('Failed to log diagnostic:', err);
        }
    };

    // ===== RACE CONDITION FIX: Capture agreementID at mount =====
    const agreementIDRef = useRef(null);

    useEffect(() => {
        // Capture agreementID when component mounts or when it changes
        if (agreementID) {
            const parsed = parseInt(agreementID, 10);
            if (!isNaN(parsed) && parsed > 0) {
                agreementIDRef.current = parsed;
                console.log('Captured agreementID on mount:', parsed);
            } else {
                console.error('Invalid agreementID on mount:', agreementID);
            }
        }
    }, [agreementID]);

    const handleFileUpload = async (file) => {
        console.log(file)
        const formData = new FormData();
        formData.append('file', file);
        formData.append('advertiserCode', advertiserCode);

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/UploadFile`, formData, {
                onUploadProgress: progressEvent => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileProgress(prevProgress => ({ ...prevProgress, [file.name]: progress }));
                }
            });
            return null;
        } catch (error) {
            console.error('Error uploading file:', error);
            setFileProgress(prev => {
                const { [file.name]: _, ...rest } = prev;
                return rest;
            });
            return {
                name: file.name,
                reason: error.response?.data?.message || 'Upload failed. Please try again.'
            };
        }
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        const blocked = files.filter(f => blockedExtensions[getExt(f.name)]);
        const allowed = files.filter(f => !blockedExtensions[getExt(f.name)]);

        const rejected = blocked.map(f => ({ name: f.name, reason: blockedExtensions[getExt(f.name)] }));

        if (rejected.length) {
            showRejectedToast(rejected);
        }

        const results = await Promise.all(allowed.map(file => handleFileUpload(file)));
        const failed = results.filter(Boolean);

        if (failed.length) {
            showRejectedToast([...rejected, ...failed]);
        }
    };

    const showRejectedToast = (items) => {
        showToast(
            <>
                <div className="mb-1">
                    {items.length === 1 ? 'This file was not uploaded:' : `These ${items.length} files were not uploaded:`}
                </div>
                {items.map(f => (
                    <div key={f.name}><strong>{f.name}</strong>: {f.reason}</div>
                ))}
            </>,
            'danger'
        );
    };


    const handleInputChange = (event) => {
        setFormData({ ...formData, [event.target.name]: event.target.value });
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const token = localStorage.getItem('token');

        // ===== LOG: Submission attempt =====
        await logDiagnostic('SUBMIT_CLICKED');

        // Check if token exists
        if (!token) {
            alert('Your session has expired. Please log in again.');
            navigate('/login');
            return;
        }
        if (isSubmitting) return;
        setIsSubmitting(true);

        // ===== CRITICAL: Capture values BEFORE any async operations =====
        const capturedAgreementID = agreementIDRef.current || parseInt(agreementID, 10);

        // ===== LOG: After capturing agreementID =====
        await logDiagnostic('AGREEMENTID_CAPTURED', {
            agreementIDFromParams: agreementID,
            agreementIDRefCurrent: agreementIDRef.current,
            capturedAgreementID: capturedAgreementID,
            capturedIsNaN: isNaN(capturedAgreementID),
            capturedLessThanOrEqualZero: capturedAgreementID <= 0
        });

        console.log('=== FORM SUBMISSION START ===');
        console.log('agreementID from useParams:', agreementID);
        console.log('agreementIDRef.current:', agreementIDRef.current);
        console.log('capturedAgreementID:', capturedAgreementID);
        console.log('user.UserID:', user?.UserID);
        console.log('advertiserCode:', advertiserCode);
        console.log('============================');

        // Validate all required fields
        if (!user?.UserID) {
            await logDiagnostic('VALIDATION_FAILED_NO_USERID', {
                hasUser: !!user,
                userKeys: user ? Object.keys(user) : [],
                userObject: user
            });
            console.error('Missing UserID', { user });
            alert('Session error: User information missing. Please log in again.');
            setIsSubmitting(false);
            return;
        }

        if (!capturedAgreementID || isNaN(capturedAgreementID) || capturedAgreementID <= 0) {
            await logDiagnostic('VALIDATION_FAILED_INVALID_AGREEMENTID', {
                agreementIDFromParams: agreementID,
                agreementIDFromParamsType: typeof agreementID,
                agreementIDRefCurrent: agreementIDRef.current,
                agreementIDRefType: typeof agreementIDRef.current,
                capturedAgreementID: capturedAgreementID,
                capturedAgreementIDType: typeof capturedAgreementID,
                isFalsy: !capturedAgreementID,
                isNaN: isNaN(capturedAgreementID),
                isLessThanOrEqualZero: capturedAgreementID <= 0,
                // URL analysis
                fullUrl: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                urlParams: Object.fromEntries(new URLSearchParams(window.location.search))
            });
            console.error('Invalid agreementID at submission', {
                agreementID,
                capturedAgreementID,
                agreementIDRef: agreementIDRef.current
            });
            alert('Session error: Invalid agreement. Please refresh the page and try again.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.contactInfo.trim()) {
            await logDiagnostic('VALIDATION_FAILED_NO_CONTACT_INFO', {
                contactInfoValue: formData.contactInfo,
                contactInfoLength: formData.contactInfo.length,
                allFormDataKeys: Object.keys(formData)
            });
            alert('Please fill in the required contact information field.');
            setIsSubmitting(false);
            return;
        }

        // ===== LOG: All validations passed =====
        await logDiagnostic('VALIDATION_PASSED', {
            capturedAgreementID: capturedAgreementID,
            userID: user.UserID
        });

        // Stored in original index order: the "Advertiser Asset Info" email template may bind by array position.
        const jsonformData = [...QUESTIONS]
            .sort((a, b) => a.index - b.index)
            .map(q => ({ index: q.index, question: q.label, answer: formData[q.name] }));

        const textData = jsonformData.map(item => `${item.question}\n${item.answer}\n\n`).join('');
        const jsonString = JSON.stringify(jsonformData);
        const textBlob = new Blob([textData], { type: 'text/plain' });

        const textFormData = new FormData();
        textFormData.append('file', textBlob, `${advertiserCode}.txt`);
        textFormData.append('advertiserCode', advertiserCode);
        try {
            // Upload file first
            await logDiagnostic('STARTING_TXT_FILE_UPLOAD');
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/UploadFile`, textFormData);
            await logDiagnostic('TXT_FILE_UPLOAD_SUCCESS');

            console.log('=== AFTER FILE UPLOAD ===');
            console.log('agreementID from useParams (may have changed):', agreementID);
            console.log('capturedAgreementID (safe, unchanged):', capturedAgreementID);
            console.log('Will use capturedAgreementID for tracker update');
            console.log('==========================');

            // Submit tracker update with the CAPTURED value
            await logDiagnostic('STARTING_TRACKER_UPDATE', {
                capturedAgreementID: capturedAgreementID,
                userID: user.UserID,
                dataLength: jsonString.length
            });

            await submitAdTrackerUpdate(jsonString, capturedAgreementID, user.UserID, token);

            await logDiagnostic('TRACKER_UPDATE_SUCCESS');

            setShowSuccessModal(true);
            clearFormData();
        } catch (error) {
            await logDiagnostic('SUBMISSION_ERROR', {
                errorMessage: error.message,
                errorStack: error.stack,
                errorResponseStatus: error.response?.status,
                errorResponseData: error.response?.data
            });

            console.error('Error in form submission:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            alert('There was an issue submitting your ad information. Please try again or contact support.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ===== RACE CONDITION FIX: Accept all values as parameters =====
    const submitAdTrackerUpdate = async (jsonString, validatedAgreementID, userID, token) => {

        // Final validation (defensive programming)
        if (!Number.isInteger(validatedAgreementID) || validatedAgreementID <= 0) {
            console.error('CRITICAL: Invalid AgreementID in submitAdTrackerUpdate', {
                validatedAgreementID,
                type: typeof validatedAgreementID
            });
            throw new Error('Invalid Agreement ID - cannot submit tracker update');
        }

        if (!userID) {
            console.error('CRITICAL: Missing UserID in submitAdTrackerUpdate');
            throw new Error('Missing User ID - cannot submit tracker update');
        }

        const payload = {
            TypeID: 3,
            AgreementID: validatedAgreementID,
            UserID: userID,
            Data: jsonString
        };

        console.log('=== SUBMITTING TRACKER UPDATE ===');
        console.log('Payload:', JSON.stringify(payload, null, 2));
        console.log('================================');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/CRM/SubmitAdTrackerUpdate`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            console.log('Ad Tracker Update submitted successfully:', response.data);
        } catch (error) {
            const status = error.response?.status;
            const data = error.response?.data;

            console.error('Tracker update failed', {
                status,
                data,
                error: error.message,
                payload
            });

            if (status === 401) {
                alert('Your session has expired. Please log in again and re-submit.');
                navigate('/login');
                throw error;
            }

            if ([500, 502, 503, 504].includes(status)) {
                alert('A server error occurred. Please try again in a few minutes.');
                throw error;
            }

            alert('There was an issue submitting your ad information. Please try again.');
            throw error;
        }
    };

    useEffect(() => {
        const warnIfLeaving = (e) => {
            if (isSubmitting) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', warnIfLeaving);
        return () => window.removeEventListener('beforeunload', warnIfLeaving);
    }, [isSubmitting]);

    const clearFormData = () => {
        setFormData({
            productDescription: '',
            slogan: '',
            contactInfo: '',
            businessName: '',
            socialMedia: '',
            certificationNumbers: '',
            specialOffers: '',
            productionNotes: ''
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setFileProgress({});
    };

    return (
        <>
            <div className="status-bg">
                <StepTracker />
                <Card className="border-0 status-description">
                    <Card.Body className="pt-0">
                        <Card.Title
                            className="step-title text-center d-flex justify-content-center align-items-center"
                        >
                            Waiting on Assets
                        </Card.Title>
                        <Card.Text>
                            To help us create the most effective ad for your business, please keep your responses brief and focus on the information you&rsquo;d most like customers to see. Since your ad will be displayed in a high-energy gym environment, we want to make sure your message is clear, eye-catching, and easy to remember. There&rsquo;s no need to provide a full description of your business... just share the key products, services, offers, or information you&rsquo;d like us to highlight. Our design team will take it from there!
                        </Card.Text>
                    </Card.Body>
                </Card>
            </div>

            <Card className="border-0 m-3">
                <Card.Body>

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Card>
                                <Card.Header className="account-card-header">
                                    <Form.Label>Upload Creative Assets (4&ndash;5 Images, Logo, or Supplied Video)</Form.Label>
                                </Card.Header>
                                <Card.Body>
                                    <p className="text-start">
                                        Please upload 4&ndash;5 high-quality images and your logo. If you have a completed video you&rsquo;d like us to use, you may upload that instead.
                                    </p>
                                    <Form.Control
                                        multiple
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                        type="file"
                                    />
                                    <Form.Text muted>
                                        Images, video, and PDF files. Zip/rar files aren't accepted, so please upload files individually.
                                    </Form.Text>
                                    {Object.entries(fileProgress).map(([fileName, progress]) => (
                                        <div key={fileName}>
                                            <p>{fileName}</p>
                                            <ProgressBar
                                                label={`${progress}%`}
                                                now={progress}
                                            />
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Form.Group>

                        {QUESTIONS.map(q => (
                            <Form.Group className="mb-3 text-start" key={q.name}>
                                <Card>
                                    <Card.Header className="account-card-header">
                                        <Form.Label>{q.prompt}</Form.Label>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form.Control
                                            as="textarea"
                                            maxLength={q.max}
                                            name={q.name}
                                            onChange={handleInputChange}
                                            required={!!q.required}
                                            rows={3}
                                            value={formData[q.name]}
                                        />
                                        <Form.Text className="d-block text-end" muted>
                                            {formData[q.name].length}/{q.max}
                                        </Form.Text>
                                    </Card.Body>
                                </Card>
                            </Form.Group>
                        ))}

                        <Button
                            disabled={isSubmitting}
                            type="submit"
                            variant="primary"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>

                    </Form>

                    <Toast
                        duration={0}
                        message={toastState.message}
                        onClose={hideToast}
                        show={toastState.show}
                        variant={toastState.variant}
                    />

                    <Modal
                        className='submit-success-modal'
                        onHide={() => setShowSuccessModal(false)}
                        show={showSuccessModal}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Success</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Your information has been successfully uploaded! The tracker will be updated shortly.</Modal.Body>
                        <Modal.Footer>
                            <Button
                                onClick={() => { setShowSuccessModal(false); navigate('/ad-account'); }}
                                variant="secondary"
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>

                </Card.Body>
            </Card >
        </>

    );
};

export default WaitingOnAssets;